import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  RefreshCw, 
  Package, 
  FileDown,
  Eye,
  Filter,
  Truck,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Download,
  Loader2,
  Trash2,
  IndianRupee
} from 'lucide-react';
import GRNCreateDialog from './grn/GRNCreateDialog';
import GRNViewDialog from './grn/GRNViewDialog';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';
import { GRNPaymentsDialog } from '@/components/grn/GRNPaymentsDialog';
import { PaymentTypeBadge } from '@/components/PaymentTypeBadge';
import { PaymentStatusBadge, grnPayment, fmtRs } from '@/components/grn/PaymentStatusBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function GRNManagement() {
  const { canCreate: canCreateGRN } = useModulePermissions();
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedGRNs, setSelectedGRNs] = useState([]);
  const [userRole, setUserRole] = useState('user');
  
  // Filters
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [dateField, setDateField] = useState('received_date'); // received_date | invoice_date
  const [datePreset, setDatePreset] = useState('all');
  // Sorting
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [paymentGrn, setPaymentGrn] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  
  useEffect(() => {
    fetchAllData();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'user');
      }
    } catch (e) {}
  }, []);
  
  async function fetchAllData() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      const [grnsRes, suppliersRes] = await Promise.all([
        axios.get(API + '/grn', { headers }),
        axios.get(API + '/suppliers', { headers }),
      ]);
      
      setGrns(grnsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (err) {
      toast.error('Failed to fetch GRN data');
    } finally {
      setLoading(false);
    }
  }
  
  function handleCreateSuccess() {
    setCreateDialogOpen(false);
    fetchAllData();
    toast.success('GRN created successfully');
  }
  
  function handleView(grn) {
    setSelectedGRN(grn);
    setViewDialogOpen(true);
  }
  
  // Filter GRNs
  const rawFiltered = grns.filter(grn => {
    // Search filter (GRN#, PO#, Supplier, Invoice#, GRN Done By)
    const t = searchTerm.toLowerCase();
    const matchesSearch = 
      grn.grn_number?.toLowerCase().includes(t) ||
      grn.po_number?.toLowerCase().includes(t) ||
      grn.supplier_name?.toLowerCase().includes(t) ||
      grn.invoice_number?.toLowerCase().includes(t) ||
      (grn.received_by_name || grn.created_by_name || '')?.toLowerCase().includes(t);
    
    // Supplier filter
    const matchesSupplier = supplierFilter === 'all' || grn.supplier_id === supplierFilter;
    
    // Date filters — applied on the selected date field (Received / Invoice date)
    let matchesDate = true;
    const dval = (grn[dateField] || '').slice(0, 10);
    if (dateFromFilter && (!dval || dval < dateFromFilter)) {
      matchesDate = false;
    }
    if (dateToFilter && (!dval || dval > dateToFilter)) {
      matchesDate = false;
    }
    
    const matchesPayment = paymentFilter === 'all' || grnPayment(grn).status === paymentFilter;
    return matchesSearch && matchesSupplier && matchesDate && matchesPayment;
  });

  const filteredGRNs = sortKey ? [...rawFiltered].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (sortKey === 'grn_done_by') { av = a.received_by_name || a.created_by_name || ''; bv = b.received_by_name || b.created_by_name || ''; }
    if (sortKey === 'invoice_date') { av = (av || '').slice(0, 10); bv = (bv || '').slice(0, 10); }
    if (typeof av === 'string' || typeof bv === 'string') {
      av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    av = av == null ? -Infinity : Number(av); bv = bv == null ? -Infinity : Number(bv);
    return sortDir === 'asc' ? av - bv : bv - av;
  }) : rawFiltered;

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key); setSortDir('desc');
    }
  };

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    const iso = (d) => d.toISOString().slice(0, 10);
    const now = new Date();
    if (preset === 'all') { setDateFromFilter(''); setDateToFilter(''); return; }
    if (preset === 'today') { setDateFromFilter(iso(now)); setDateToFilter(iso(now)); return; }
    if (preset === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); setDateFromFilter(iso(y)); setDateToFilter(iso(y)); return; }
    if (preset === 'week') { const s = new Date(now); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); setDateFromFilter(iso(s)); setDateToFilter(iso(now)); return; }
    if (preset === 'month') { setDateFromFilter(iso(new Date(now.getFullYear(), now.getMonth(), 1))); setDateToFilter(iso(now)); return; }
    // custom → keep whatever is in the inputs
  };

  const fmtDMY = (d) => {
    if (!d) return '—';
    const s = String(d).slice(0, 10).split('-');
    return s.length === 3 ? `${s[2]}-${s[1]}-${s[0]}` : '—';
  };
  const fmtINR = (v) => (v == null || isNaN(Number(v))) ? '—' :
    '₹' + Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  // Summary stats
  const totalGRNs = filteredGRNs.length;
  const totalReceived = filteredGRNs.reduce((sum, g) => sum + (g.total_accepted || 0), 0);
  const withShortage = filteredGRNs.filter(g => g.has_shortage).length;
  const withDamaged = filteredGRNs.filter(g => g.has_damaged_stock).length;
  
  const canExport = ['admin', 'manager', 'staff', 'accountant'].includes(userRole);

  const handleExport = async (exportSelected = false) => {
    setExporting(true);
    try {
      const payload = {};
      if (exportSelected && selectedGRNs.length > 0) {
        payload.grn_ids = selectedGRNs;
      } else {
        if (supplierFilter !== 'all') payload.supplier_id = supplierFilter;
        if (dateFromFilter) payload.date_from = dateFromFilter;
        if (dateToFilter) payload.date_to = dateToFilter;
        if (searchTerm) payload.search = searchTerm;
      }
      
      const response = await axios.post(API + '/export/grn', payload, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GRN_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportSelected ? selectedGRNs.length : filteredGRNs.length} GRN records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // Per-line MRP reconciliation export (Feb 2026). Deliberately separate from
  // the standard per-GRN export so Procurement can pick the granularity that
  // matches their audit workflow. Same filter payload contract as `/export/grn`.
  const handleExportMrpReconciliation = async (format = 'xlsx') => {
    setExporting(true);
    try {
      const payload = {};
      if (selectedGRNs.length > 0) {
        payload.grn_ids = selectedGRNs;
      } else {
        if (supplierFilter !== 'all') payload.supplier_id = supplierFilter;
        if (dateFromFilter) payload.date_from = dateFromFilter;
        if (dateToFilter) payload.date_to = dateToFilter;
        if (searchTerm) payload.search = searchTerm;
      }
      const endpoint = format === 'csv'
        ? '/export/grn-mrp-reconciliation-csv'
        : '/export/grn-mrp-reconciliation';
      const response = await axios.post(API + endpoint, payload, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `GRN_MRP_Reconciliation_${new Date().toISOString().slice(0, 10)}.${format}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`MRP reconciliation ${format.toUpperCase()} downloaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export MRP reconciliation');
    } finally {
      setExporting(false);
    }
  };

  const toggleGRNSelection = (grnId) => {
    setSelectedGRNs(prev => 
      prev.includes(grnId) ? prev.filter(id => id !== grnId) : [...prev, grnId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedGRNs.length === filteredGRNs.length) {
      setSelectedGRNs([]);
    } else {
      setSelectedGRNs(filteredGRNs.map(g => g.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedGRNs.length === 0) return;
    if (!window.confirm(`Delete ${selectedGRNs.length} GRN(s)? Stock will be reversed. This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(API + '/grn/bulk-delete', { ids: selectedGRNs }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success(`${selectedGRNs.length} GRN(s) deleted`);
      setSelectedGRNs([]);
      fetchAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleDeleteSingle = async (grnId) => {
    if (!window.confirm('Delete this GRN? Stock will be reversed.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(API + '/grn/' + grnId, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('GRN deleted');
      fetchAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Loading GRN Data...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="grn-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">GRN Management</h1>
          <p className="text-slate-600 mt-1">Goods Receipt Notes - Inward Stock Entry</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <>
              {selectedGRNs.length > 0 && (
                <>
                <Button variant="outline" size="sm" onClick={() => handleExport(true)} disabled={exporting} className="gap-1">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Export Selected ({selectedGRNs.length})
                </Button>
                <CsvDownloadButton onDownload={() => handleExport(true)} testId="grn-export-selected-csv" disabled={exporting} />
                </>
              )}
              {selectedGRNs.length > 0 && (
                <Button variant="outline" size="sm" onClick={handleDeleteSelected} className="gap-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-selected-grn">
                  <Trash2 className="w-4 h-4" />
                  Delete Selected ({selectedGRNs.length})
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => handleExport(false)} disabled={exporting} className="gap-1">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export to Excel
              </Button>
              <CsvDownloadButton onDownload={() => handleExport(false)} testId="grn-export-csv" disabled={exporting} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportMrpReconciliation('xlsx')}
                disabled={exporting}
                className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                title="Per-line PO MRP vs GRN MRP reconciliation (Excel)"
                data-testid="export-mrp-reconciliation-xlsx"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                MRP Reconciliation (XLSX)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportMrpReconciliation('csv')}
                disabled={exporting}
                className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                title="Per-line PO MRP vs GRN MRP reconciliation (CSV)"
                data-testid="export-mrp-reconciliation-csv"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                MRP Reconciliation (CSV)
              </Button>
            </>
          )}
          <Button onClick={fetchAllData} variant="outline" size="sm" className="gap-1">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {canCreateGRN('grn') && (
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2" data-testid="create-grn-btn">
              <Plus size={18} />
              Create GRN
            </Button>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Total GRNs</p>
                <p className="text-2xl font-bold text-blue-800">{totalGRNs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Items Received</p>
                <p className="text-2xl font-bold text-green-800">{totalReceived.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-yellow-600 font-medium">With Shortage</p>
                <p className="text-2xl font-bold text-yellow-800">{withShortage}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600 font-medium">With Damaged</p>
                <p className="text-2xl font-bold text-red-800">{withDamaged}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-600">Filters:</span>
            </div>
            
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search GRN#, PO#, Supplier, Invoice#, Done By..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="grn-search-input"
              />
            </div>
            
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-48 h-9" data-testid="supplier-filter">
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-44 h-9" data-testid="payment-status-filter">
                <SelectValue placeholder="All Payments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
                <SelectItem value="fully_paid">Fully Paid</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-400" />
              <Select value={dateField} onValueChange={setDateField}>
                <SelectTrigger className="w-36 h-9" data-testid="grn-date-field-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="received_date">Received Date</SelectItem>
                  <SelectItem value="invoice_date">Invoice Date</SelectItem>
                </SelectContent>
              </Select>
              <Select value={datePreset} onValueChange={applyDatePreset}>
                <SelectTrigger className="w-32 h-9" data-testid="grn-date-preset-select">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={dateFromFilter}
                onChange={(e) => { setDateFromFilter(e.target.value); setDatePreset('custom'); }}
                className="h-9 w-36"
                placeholder="From Date"
              />
              <span className="text-gray-400">-</span>
              <Input
                type="date"
                value={dateToFilter}
                onChange={(e) => { setDateToFilter(e.target.value); setDatePreset('custom'); }}
                className="h-9 w-36"
                placeholder="To Date"
              />
            </div>
            
            {(searchTerm || supplierFilter !== 'all' || paymentFilter !== 'all' || dateFromFilter || dateToFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSupplierFilter('all');
                  setPaymentFilter('all');
                  setDateFromFilter('');
                  setDateToFilter('');
                  setDatePreset('all');
                  setDateField('received_date');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* GRN List */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[calc(100vh-14rem)]" data-testid="grn-table-scroll">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]" data-testid="grn-table-head">
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    GRN Number
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    PO Number
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Supplier
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold cursor-pointer select-none hover:text-blue-600"
                      onClick={() => toggleSort('invoice_date')} data-testid="sort-invoice-date">
                    Invoice Date{sortKey === 'invoice_date' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Invoice Number
                  </th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold cursor-pointer select-none hover:text-blue-600"
                      onClick={() => toggleSort('bill_value')} data-testid="sort-bill-value">
                    Bill Value{sortKey === 'bill_value' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold cursor-pointer select-none hover:text-blue-600"
                      onClick={() => toggleSort('grand_total_incl_gst')} data-testid="sort-grn-value">
                    GRN Value{sortKey === 'grand_total_incl_gst' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Paid</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Outstanding</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Payment</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Received Date
                  </th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Items
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold cursor-pointer select-none hover:text-blue-600"
                      onClick={() => toggleSort('grn_done_by')} data-testid="sort-grn-done-by">
                    GRN Done By{sortKey === 'grn_done_by' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Status
                  </th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Flags
                  </th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredGRNs.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center py-12 text-slate-500">
                      <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No GRNs found</p>
                      <p className="text-sm mt-1">Create a new GRN to receive stock from Purchase Orders</p>
                    </td>
                  </tr>
                ) : (
                  filteredGRNs.map((grn) => (
                    <tr key={grn.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`grn-row-${grn.id}`}>
                      <td className="py-3 px-4">
                        <span className="font-mono font-semibold text-blue-600">{grn.grn_number}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-gray-600 whitespace-nowrap">{grn.po_number}</span>
                        <div className="mt-1"><PaymentTypeBadge type={grn.po_payment_type} testId={`grn-po-payment-type-${grn.id}`} /></div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{grn.supplier_name}</p>
                          {grn.retailer_name && (
                            <p className="text-xs text-gray-500">For: {grn.retailer_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600" data-testid={`grn-invoice-date-${grn.id}`}>
                        {fmtDMY(grn.invoice_date)}
                      </td>
                      <td className="py-3 px-4" data-testid={`grn-invoice-number-${grn.id}`}>
                        {grn.invoice_number
                          ? <span className="font-mono text-gray-700">{grn.invoice_number}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-purple-700" data-testid={`grn-bill-value-${grn.id}`}>
                        {fmtINR(grn.bill_value)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-emerald-700" data-testid={`grn-value-${grn.id}`}>
                        {fmtINR(grn.grand_total_incl_gst)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-700" data-testid={`grn-paid-${grn.id}`}>{fmtRs(grnPayment(grn).paid)}</td>
                      <td className={`py-3 px-4 text-right font-medium ${grnPayment(grn).outstanding > 0.01 ? 'text-red-600' : 'text-slate-400'}`} data-testid={`grn-outstanding-${grn.id}`}>
                        {fmtRs(grnPayment(grn).outstanding)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button type="button" onClick={() => setPaymentGrn(grn)} title="View / record payments" data-testid={`grn-payment-status-${grn.id}`}>
                          <PaymentStatusBadge status={grnPayment(grn).status} />
                        </button>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {fmtDMY(grn.received_date)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="text-sm">
                          <span className="font-semibold text-green-600">{grn.total_accepted || 0}</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-gray-600">{grn.total_ordered || 0}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700" data-testid={`grn-done-by-${grn.id}`}>
                        {grn.received_by_name || grn.created_by_name || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={
                          grn.status === 'completed' ? 'bg-green-100 text-green-800' :
                          grn.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {(grn.status || 'completed').toUpperCase()}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center gap-1">
                          {grn.has_shortage && (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-300 text-xs">
                              Shortage
                            </Badge>
                          )}
                          {grn.has_damaged_stock && (
                            <Badge variant="outline" className="text-red-600 border-red-300 text-xs">
                              Damaged
                            </Badge>
                          )}
                          {grn.has_alternate_stock && (
                            <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                              Alternate
                            </Badge>
                          )}
                          {!grn.has_shortage && !grn.has_damaged_stock && !grn.has_alternate_stock && (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleView(grn)}
                            data-testid={`view-grn-${grn.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setPaymentGrn(grn)} title="Payments"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-testid={`grn-payments-btn-${grn.id}`}>
                            <IndianRupee size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // PDF download - placeholder
                              toast.info('Generating PDF...');
                              setTimeout(() => {
                                generateGRNPDF(grn);
                              }, 500);
                            }}
                            data-testid={`download-grn-${grn.id}`}
                          >
                            <FileDown size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSingle(grn.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-grn-${grn.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Create GRN Dialog */}
      <GRNCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />
      
      {/* View GRN Dialog */}
      <GRNViewDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        grn={selectedGRN}
      />
      <GRNPaymentsDialog
        open={!!paymentGrn}
        grn={paymentGrn}
        isAdmin={userRole === 'admin'}
        onClose={() => setPaymentGrn(null)}
        onChanged={(d) => setGrns((prev) => prev.map((g) => (g.id === d.grn_id ? { ...g, amount_paid: d.amount_paid, outstanding_amount: d.outstanding_amount, advance_amount: d.advance_amount, payment_status: d.payment_status } : g)))}
      />
    </div>
  );
}

// Helper function to generate PDF
function generateGRNPDF(grn) {
  // Create a printable HTML content
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Please allow popups to download PDF');
    return;
  }
  
  const itemsHTML = grn.items.map(item => `
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd;">${item.product_name}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.ordered_quantity}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.received_quantity}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.shortage_quantity || 0}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.damaged_quantity || 0}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: center; font-weight: bold;">${item.accepted_quantity || item.received_quantity}</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${item.batch_number || '-'}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${item.po_mrp != null ? '₹' + Number(item.po_mrp).toFixed(2) : '-'}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${item.grn_mrp != null ? '₹' + Number(item.grn_mrp).toFixed(2) : '-'}</td>
      <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${item.b2b_price_incl_gst != null ? '₹' + Number(item.b2b_price_incl_gst).toFixed(2) : '-'}</td>
    </tr>
  `).join('');
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>GRN - ${grn.grn_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .header h1 { margin: 0; color: #333; }
        .header p { color: #666; margin: 5px 0; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
        .info-box { background: #f9f9f9; padding: 15px; border-radius: 5px; }
        .info-box h3 { margin: 0 0 10px 0; color: #333; font-size: 14px; }
        .info-box p { margin: 5px 0; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th { background: #f0f0f0; padding: 10px; border: 1px solid #ddd; text-align: left; font-size: 12px; }
        .summary { margin-top: 20px; padding: 15px; background: #f9f9f9; border-radius: 5px; }
        .summary h3 { margin: 0 0 10px 0; }
        .flags { margin-top: 15px; }
        .flag { display: inline-block; padding: 3px 8px; border-radius: 3px; margin-right: 5px; font-size: 11px; }
        .flag-shortage { background: #fef3c7; color: #92400e; }
        .flag-damaged { background: #fee2e2; color: #991b1b; }
        .flag-alternate { background: #dbeafe; color: #1e40af; }
        .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #999; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>GOODS RECEIPT NOTE</h1>
        <p><strong>${grn.grn_number}</strong></p>
        <p>Generated on: ${new Date().toLocaleDateString('en-IN')}</p>
      </div>
      
      <div class="info-grid">
        <div class="info-box">
          <h3>GRN Details</h3>
          <p><strong>GRN Number:</strong> ${grn.grn_number}</p>
          <p><strong>PO Number:</strong> ${grn.po_number}</p>
          <p><strong>Received Date:</strong> ${new Date(grn.received_date).toLocaleDateString('en-IN')}</p>
          <p><strong>Received By:</strong> ${grn.received_by_name || grn.received_by}</p>
        </div>
        <div class="info-box">
          <h3>Supplier Details</h3>
          <p><strong>Supplier:</strong> ${grn.supplier_name}</p>
          ${grn.retailer_name ? `<p><strong>Retailer/Franchise:</strong> ${grn.retailer_name}</p>` : ''}
          ${grn.franchise_id ? `<p><strong>Franchise ID:</strong> ${grn.franchise_id}</p>` : ''}
        </div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Product</th>
            <th style="text-align: center;">Ordered</th>
            <th style="text-align: center;">Received</th>
            <th style="text-align: center;">Shortage</th>
            <th style="text-align: center;">Damaged</th>
            <th style="text-align: center;">Accepted</th>
            <th>Batch #</th>
            <th style="text-align: right;">PO MRP</th>
            <th style="text-align: right;">GRN MRP</th>
            <th style="text-align: right;">B2B Inward (Incl GST)</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>
      
      <div class="summary">
        <h3>Summary</h3>
        <p><strong>Total Ordered:</strong> ${grn.total_ordered}</p>
        <p><strong>Total Received:</strong> ${grn.total_received}</p>
        <p><strong>Total Accepted:</strong> ${grn.total_accepted}</p>
        ${grn.total_shortage > 0 ? `<p><strong>Total Shortage:</strong> ${grn.total_shortage}</p>` : ''}
        ${grn.total_damaged > 0 ? `<p><strong>Total Damaged:</strong> ${grn.total_damaged}</p>` : ''}
        
        <div class="flags">
          ${grn.has_shortage ? '<span class="flag flag-shortage">Shortage</span>' : ''}
          ${grn.has_damaged_stock ? '<span class="flag flag-damaged">Damaged</span>' : ''}
          ${grn.has_alternate_stock ? '<span class="flag flag-alternate">Alternate Stock</span>' : ''}
        </div>
        
        ${grn.disclaimer_remarks ? `
          <div style="margin-top: 15px; padding: 10px; background: #fff3cd; border-radius: 5px;">
            <strong>Disclaimer/Remarks:</strong><br/>
            ${grn.disclaimer_remarks}
          </div>
        ` : ''}
        
        ${grn.notes ? `<p style="margin-top: 10px;"><strong>Notes:</strong> ${grn.notes}</p>` : ''}
      </div>
      
      <div class="footer">
        <p>This is a computer-generated document. No signature required.</p>
      </div>
      
      <script>
        window.onload = function() {
          window.print();
        }
      </script>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
}

export default GRNManagement;
